export const FONT_FAMILIES = [
  'Verdana',
  'Andale Mono',
  'Arial',
  'Arial Black',
  'Book Antiqua',
  'Comic Sans MS',
  'Courier New',
  'Noto Sans',
  'Sans-serif',
  'Serif',
  'Tahoma',
  'Terminal',
  'Times New Roman',
  'Trebuchet MS',
];

export const FONT_SIZES = [
  '10px', '11px', '12px', '13px', '14px', '15px', '16px',
  '18px', '20px', '22px', '24px', '28px', '32px', '36px',
];

export const BLOCK_TYPES = [
  { label: 'Paragraph', tag: 'p' },
  { label: 'Heading 1', tag: 'h1' },
  { label: 'Heading 2', tag: 'h2' },
  { label: 'Heading 3', tag: 'h3' },
  { label: 'Heading 4', tag: 'h4' },
  { label: 'Heading 5', tag: 'h5' },
  { label: 'Heading 6', tag: 'h6' },
  { label: 'div', tag: 'div' },
  { label: 'pre', tag: 'pre' },
];

export const LINE_HEIGHTS = ['1', '1.15', '1.5', '2'];

export const DEFAULT_FONT_FAMILY = 'Verdana';
export const DEFAULT_FONT_SIZE = '16px';
export const DEFAULT_LINE_HEIGHT = '1.5';
export const DEFAULT_TEXT_COLOR = '#000000';

export const DEFAULT_PARAGRAPH_STYLE =
  'margin:0 0 12px 0;font-family:verdana,geneva,sans-serif;font-size:16px;line-height:1.5;';

export const MERGE_FIELD_PATTERN = /\{\{[^}]+\}\}/g;

export function normalizeHexColor(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let cleaned = hex.trim();
  if (!cleaned.startsWith('#')) cleaned = `#${cleaned}`;
  if (/^#[0-9a-fA-F]{3}$/.test(cleaned)) {
    const [, r, g, b] = cleaned.match(/^#(.)(.)(.)$/);
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  if (/^#[0-9a-fA-F]{6}$/.test(cleaned)) {
    return cleaned.toLowerCase();
  }
  return null;
}

export function hexToRgb(hex) {
  const normalized = (normalizeHexColor(hex) || '#000000').replace('#', '');
  const int = parseInt(normalized, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
}

export function hexToRgba(hex, alpha = 1) {
  const { r, g, b } = hexToRgb(hex);
  const a = Number.isFinite(alpha) ? alpha : 1;
  return { r, g, b, a };
}

export function rgbaToHex(r, g, b) {
  return rgbToHex(r, g, b);
}

export function rgbToHex(r, g, b) {
  const toHex = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function rgbaString(r, g, b, a = 1) {
  return `${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${Number(a).toFixed(2).replace(/\.00$/, '')}`;
}

export function parseRgbaString(value) {
  const parts = (value || '').split(',').map((p) => p.trim());
  if (parts.length < 3) return { r: 0, g: 0, b: 0, a: 1 };
  return {
    r: Number(parts[0]) || 0,
    g: Number(parts[1]) || 0,
    b: Number(parts[2]) || 0,
    a: parts[3] !== undefined ? Number(parts[3]) : 1,
  };
}

export function hslToHex(h, s, l) {
  s /= 100;
  l /= 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return rgbToHex(f(0) * 255, f(8) * 255, f(4) * 255);
}

export function hexToHsv(hex) {
  const { r, g, b } = hexToRgb(normalizeHexColor(hex) || '#000000');
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const delta = max - min;
  let h = 0;
  const s = max === 0 ? 0 : (delta / max) * 100;
  const v = max * 100;

  if (delta !== 0) {
    if (max === rn) h = ((gn - bn) / delta) % 6;
    else if (max === gn) h = (bn - rn) / delta + 2;
    else h = (rn - gn) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s, v };
}

export function hsvToHex(h, s, v) {
  const saturation = s / 100;
  const value = v / 100;
  const c = value * saturation;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = value - c;
  let rp = 0;
  let gp = 0;
  let bp = 0;

  if (h < 60) {
    rp = c; gp = x;
  } else if (h < 120) {
    rp = x; gp = c;
  } else if (h < 180) {
    gp = c; bp = x;
  } else if (h < 240) {
    gp = x; bp = c;
  } else if (h < 300) {
    rp = x; bp = c;
  } else {
    rp = c; bp = x;
  }

  return rgbToHex((rp + m) * 255, (gp + m) * 255, (bp + m) * 255);
}

export function buildLinkHtml({ url, text, openInNewTab }) {
  const safeUrl = (url || '').trim();
  const label = (text || safeUrl).trim();
  const target = openInNewTab ? ' target="_blank" rel="noopener noreferrer"' : '';
  return `<a href="${safeUrl}"${target}>${label}</a>`;
}

export function buildImageHtml({ url, alt = '', width }) {
  const safeUrl = (url || '').trim();
  const widthStyle = width ? ` width="${width}"` : '';
  const style = 'max-width:100%;height:auto;display:block;';
  return `<img src="${safeUrl}" alt="${alt}" style="${style}"${widthStyle} />`;
}

export function preserveMergeFieldsInHtml(html) {
  return html || '';
}

export function stripHtmlForCount(html) {
  return (html || '').replace(/<[^>]*>/g, '');
}

export function countWords(html) {
  const text = stripHtmlForCount(html).trim();
  if (!text) return 0;
  return text.split(/\s+/).filter(Boolean).length;
}

export function ensureEditorHtml(html) {
  const trimmed = (html || '').trim();
  if (!trimmed) {
    return `<p style="${DEFAULT_PARAGRAPH_STYLE}"><br></p>`;
  }
  return trimmed;
}

export function applyFontFamilyCss(fontFamily) {
  const stack = fontFamily === 'Sans-serif'
    ? 'sans-serif'
    : fontFamily === 'Serif'
      ? 'serif'
      : `${fontFamily}, sans-serif`;
  return stack;
}

export function styleObjectToString(styles) {
  return Object.entries(styles)
    .map(([key, value]) => `${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}:${value}`)
    .join(';');
}

export function saveSelection(editorEl) {
  if (!editorEl || typeof window === 'undefined') return null;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!editorEl.contains(range.commonAncestorContainer)) return null;
  return range.cloneRange();
}

export function restoreSelection(savedRange) {
  if (!savedRange || typeof window === 'undefined') return false;
  const selection = window.getSelection();
  if (!selection) return false;
  selection.removeAllRanges();
  selection.addRange(savedRange);
  return true;
}

function camelizeStyleKey(key) {
  return key.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
}

function normalizeStyleObject(styles) {
  return Object.entries(styles || {}).reduce((acc, [key, value]) => {
    if (value == null || value === '') return acc;
    acc[camelizeStyleKey(key)] = value;
    return acc;
  }, {});
}

export function applyInlineStyleToSelection(editorEl, styleObject, savedRange) {
  if (!editorEl || typeof window === 'undefined') return null;

  editorEl.focus();
  const selection = window.getSelection();
  if (!selection) return null;

  if (savedRange) {
    restoreSelection(savedRange);
  }

  if (selection.rangeCount === 0) return null;

  let range = selection.getRangeAt(0);
  if (!editorEl.contains(range.commonAncestorContainer)) {
    range = document.createRange();
    range.selectNodeContents(editorEl);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  const styles = normalizeStyleObject(styleObject);
  if (Object.keys(styles).length === 0) return null;

  if (range.collapsed) {
    const span = document.createElement('span');
    Object.assign(span.style, styles);
    span.appendChild(document.createTextNode('\u200B'));
    range.insertNode(span);
    const caret = document.createRange();
    caret.setStart(span.firstChild, 1);
    caret.collapse(true);
    selection.removeAllRanges();
    selection.addRange(caret);
    return span;
  }

  const contents = range.extractContents();
  const span = document.createElement('span');
  Object.assign(span.style, styles);
  span.appendChild(contents);
  range.insertNode(span);

  const applied = document.createRange();
  applied.selectNodeContents(span);
  selection.removeAllRanges();
  selection.addRange(applied);
  return span;
}

export function applyTextColor(editorEl, color, savedRange) {
  const hex = normalizeHexColor(color);
  if (!hex) return null;
  return applyInlineStyleToSelection(editorEl, { color: hex }, savedRange);
}

export function applyBackgroundColor(editorEl, color, savedRange) {
  const hex = normalizeHexColor(color);
  if (!hex) return null;
  return applyInlineStyleToSelection(editorEl, { backgroundColor: hex }, savedRange);
}

export function applyStyleToSelection(editorEl, styles, savedRange) {
  return applyInlineStyleToSelection(editorEl, styles, savedRange);
}

export function applyBlockFormat(editorEl, tag) {
  if (!editorEl) return;
  editorEl.focus();
  document.execCommand('formatBlock', false, `<${tag}>`);
}

export function applyLineHeight(editorEl, lineHeight) {
  applyStyleToSelection(editorEl, { lineHeight });
}

export function getSelectedText(editorEl) {
  if (!editorEl || typeof window === 'undefined') return '';
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return '';
  return selection.toString();
}

export function insertHtmlAtCursor(editorEl, html) {
  if (!editorEl) return;
  editorEl.focus();
  document.execCommand('insertHTML', false, html);
}

export function execEditorCommand(command, value = null) {
  if (typeof document === 'undefined') return;
  document.execCommand('styleWithCSS', false, 'true');
  document.execCommand(command, false, value);
}
